<template>
  <div class="special-orders-slider">
    <page-section
      :title="{
        displayText: $t('Our works'),
        __component: StrapiComponentType.link,
      }"
      :has-container="false"
      title-class="!text-2xl"
      top-class="!pt-11 !pb-5 md:!pt-10 md:!pb-3 lg:!pb-7"
    >
      <template #rightContent>
        <swiper-navigations
          :instance="swiperInst"
          class="special-orders-slider-navigations lg:!hidden"
        />
      </template>
      <sf-button
        variant="tertiary"
        aria-label="prev button"
        :disabled="swiperInst?.isBeginning || false"
        class="special-orders-slider-action max-lg:hidden"
        @click="swiperInst.slidePrev()"
      >
        <icons-icon-chevron-down size="lg" class="icon rotate-180" />
      </sf-button>
      <div v-if="images.length" class="special-orders-slider-main">
        <swiper
          v-bind="currentOptions as any"
          class="special-orders-slider-swiper"
          @swiper="setInstance"
        >
          <swiper-slide
            v-for="(item, idx) in images"
            :key="idx"
            class="special-orders-slider-item"
          >
            <strapi-image-component
              :image="item"
              :alt="
                item?.alternativeText || `our works slider image number ${idx}`
              "
              class="special-orders-slider-image"
              sizes="xs:140px md:210px lg:340px"
              @click="openViewer(idx)"
            />
          </swiper-slide>
        </swiper>
      </div>
      <sf-button
        variant="tertiary"
        aria-label="next button"
        :disabled="swiperInst?.isEnd || false"
        class="special-orders-slider-action max-lg:hidden"
        @click="swiperInst.slideNext()"
      >
        <icons-icon-chevron-down size="lg" class="icon" />
      </sf-button>
    </page-section>
  </div>
</template>

<script setup lang="ts">
import type { SwiperOptions } from "swiper/types";
import { SPECIAL_ORDERS_SLIDER_OPTIONS } from "../variables";
import { StrapiComponentType, type StrapiImage } from "~/types/strapi";
import { StrapiImage as StrapiImageComponent } from "~/components/strapi";

const props = defineProps<{ images: StrapiImage[] }>();

const { uploadsUrl } = useStrapiExtend();
const { openImageViewer } = useImageViewer();
const { setInstance, swiper: swiperInst } = useSwiperInstance();

const currentOptions = ref<SwiperOptions>(SPECIAL_ORDERS_SLIDER_OPTIONS);

function openViewer(initialIndex: number): void {
  const preparedImages = props.images.map((i) => ({
    largeUrl: uploadsUrl(i.url),
    alt: i.alternativeText || "",
  }));

  openImageViewer(initialIndex, preparedImages);
}
</script>

<style lang="scss">
.special-orders-slider {
  @apply lg:w-[340px];

  &-main {
    @apply w-full pb-5 pt-4 md:pb-[30px] lg:p-0;
  }

  &-image {
    @apply h-full object-contain;
  }

  &-item {
    @apply max-lg:h-[136px] max-lg:w-[136px] md:h-[209px] md:w-[209px];
    @apply max-lg:cursor-pointer max-lg:border max-lg:border-solid max-lg:border-neutral-200;
  }

  &-action {
    @apply lg:w-full;
  }

  &-main {
    @apply lg:my-2;
  }

  &-swiper {
    @apply lg:h-full lg:max-h-[1036px];
  }

  &-item {
    @apply lg:h-[340px] lg:w-[340px];
  }
}
</style>
